/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { isSelectionItemComponent, useTranslate } from "@adapptio/lib-components";
import {
	isSelectionComponentEvent,
	isSelectionDataSourceEvent,
	isSelectionItemDataSource,
	isSelectionItemOutlet,
	isSelectionItemParameter,
	isSelectionItemParameterAny,
	TViewSelectionItemOrEvent
} from "./ViewEditSelection";
import {
	isModel,
	isSchemaComponentModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaDataSourceModel,
	isSchemaFlowNodeListModel,
	isSchemaFlowNodeModel,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { isFunction } from "@adapptio/lib-shared";
import { InspectComponent } from "../../../ui/components/Inspector/InspectComponent";
import { InspectDataSource } from "../../../ui/components/Inspector/InspectDataSource";
import { InspectMapEntry } from "../../../ui/components/Inspector/InspectMapEntry";
import { InspectSingleNode } from "../../../ui/components/Inspector/InspectSingleNode";
import { InspectFlowNode } from "../../../ui/components/Inspector/InspectFlowNode";

export interface IInspectViewSelectionItemProps {
	selectionItem: TViewSelectionItemOrEvent;
	getNodeById: (nodeId: number) => TGenericModelNode;
}

const pathComponent = [ "$component" ];
const pathDataSource = [ "$dataSource" ];
const pathFlowNode = [ "$flowNode" ];
const pathParameter = [ "$param" ];
const pathOutlet = [ "$outlet" ];
const pathNode = [ "$node" ];

export const InspectViewSelectionItem: React.FC<IInspectViewSelectionItemProps> = ({
	selectionItem,
	getNodeById
}) => {
	const t = useTranslate();

	const getModelNodeById = useCallback<IInspectViewSelectionItemProps["getNodeById"]>(
		(nodeId) => {
			if (!isFunction(getNodeById)) {
				console.error("Property 'getNodeById' is not a function.");
				return null;
			}

			return getNodeById(nodeId);
		},
		[ getNodeById ]
	);

	// Return proper inspector for selected item
	if (isSelectionItemComponent(selectionItem)) {
		const model = getModelNodeById(selectionItem.nodeId);

		if (!isSchemaComponentModel(model)) {
			return null;
		}

		return <InspectComponent model={model} depth={0} path={pathComponent} />;
	}

	if (isSelectionItemDataSource(selectionItem)) {
		const model = getModelNodeById(selectionItem.nodeId);

		if (!isSchemaDataSourceModel(model)) {
			return null;
		}

		return <InspectDataSource model={model} depth={0} path={pathDataSource} />;
	}

	if (isSelectionItemParameter(selectionItem) || isSelectionItemParameterAny(selectionItem)) {
		const parentModel = getModelNodeById(selectionItem.parentMapModelId);
		const modelEntry = selectionItem.mapEntry;

		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(modelEntry)) {
			return null;
		}

		return (
			<InspectMapEntry
				model={parentModel}
				modelEntry={modelEntry}
				path={pathParameter}
				depth={0}
				title={t("editor", "Parameter")}
			/>
		);
	}

	if (isSelectionItemOutlet(selectionItem)) {
		const parentModel = getModelNodeById(selectionItem.parentMapModelId);
		const modelEntry = selectionItem.mapEntry;

		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(modelEntry)) {
			return null;
		}

		return (
			<InspectMapEntry
				model={parentModel}
				modelEntry={modelEntry}
				path={pathOutlet}
				depth={0}
				title={t("editor", "Outlet")}
			/>
		);
	}

	if (isSelectionComponentEvent(selectionItem)) {
		const nodeList = getModelNodeById(selectionItem?.nodeListModelId);
		const node = getModelNodeById(selectionItem?.nodeId);

		if (isSchemaFlowNodeModel(node) && isSchemaFlowNodeListModel(nodeList)) {
			return <InspectFlowNode model={node} nodeList={nodeList} depth={0} path={pathFlowNode} />;
		}
	}

	if (isSelectionDataSourceEvent(selectionItem)) {
		const nodeList = getModelNodeById(selectionItem?.nodeListModelId);
		const node = getModelNodeById(selectionItem?.nodeId);

		if (isSchemaFlowNodeModel(node) && isSchemaFlowNodeListModel(nodeList)) {
			return <InspectFlowNode model={node} nodeList={nodeList} depth={0} path={pathFlowNode} />;
		}
	}

	const selectedModel = getModelNodeById(selectionItem?.nodeId);

	if (isModel(selectedModel)) {
		return <InspectSingleNode model={selectedModel} depth={0} path={pathNode} />;
	}

	return null;
};
